import React, { useCallback, useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { supabase } from "../services/supabaseClient";
import collocations from "../data/collocations.json";
import {
  getReadingFontFamilyStyle,
  getReadingFontSizeClass,
  getReadingPreferences,
  READING_PREFERENCES_UPDATED_EVENT,
  ReadingFontFamily,
  ReadingPreferences,
  ReadingTheme,
  saveReadingPreferences,
} from "../services/readingPreferences";
import { buildReaderDiff } from "../services/readerDiff";
import { useReadingPosition } from "../hooks/useReadingPosition";
import ReadingFeedback from "./ReadingFeedback";

// Props interface for the Reader component
interface ReaderProps {
  heading: string;
  simplifiedContent: string;
  injectedVocab: string[];
  userId: string;
  originalContentOverride: string;
  readingSectionId: string;
  adjustedReadingSectionId: string;
  adaptationLevelNotes?: string | null;
  sourceUrl?: string | null;
  sourceDomain?: string | null;
  debugModeEnabled?: boolean;
  targetLevel?: string;
  backLabel?: string;
  hasPreviousSection?: boolean;
  hasNextSection?: boolean;
  onPreviousSection?: () => void;
  onNextSection?: () => void;
  onFinishReading: () => void;
}

const readerAllowedTags = [
  "b",
  "br",
  "em",
  "h2",
  "h3",
  "h4",
  "i",
  "li",
  "ol",
  "p",
  "span",
  "strong",
  "ul",
];

const readerAllowedAttributes = ["class", "data-word", "title"];

const sanitizeReaderHtml = (html: string): string =>
  DOMPurify.sanitize(html, {
    ALLOWED_TAGS: readerAllowedTags,
    ALLOWED_ATTR: readerAllowedAttributes,
  });

const sanitizeReaderDiffHtml = (html: string): string =>
  DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [...readerAllowedTags, "del", "ins"],
    ALLOWED_ATTR: [
      ...readerAllowedAttributes,
      "aria-label",
      "data-reader-diff",
    ],
  });

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const escapeHtmlAttribute = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

type LookupStatus = "idle" | "loading" | "success" | "error";

interface DictionaryDefinition {
  definition?: string;
  example?: string;
  synonyms?: string[];
  antonyms?: string[];
}

interface DictionaryMeaning {
  partOfSpeech?: string;
  definitions?: DictionaryDefinition[];
  synonyms?: string[];
  antonyms?: string[];
}

interface DictionaryLookupData {
  word?: string;
  matched_word?: string;
  phonetic?: string;
  pronunciations?: Array<{
    text?: string;
    audio_url?: string;
  }>;
  meanings?: DictionaryMeaning[];
  sourceUrls?: string[];
  definition_source?: string;
  message?: string;
  error?: string;
}

interface CachedLookup {
  definition: string;
  data?: DictionaryLookupData | null;
  status: Exclude<LookupStatus, "idle" | "loading">;
}

interface ReadingCompletionResponse {
  completed?: boolean;
  already_completed?: boolean;
}

interface LookupPosition {
  x: number;
  y: number;
  placement: "above" | "below";
  maxHeight: number;
}

const extractSingleLookupWord = (value: string): string | null => {
  const matches = value.match(/[A-Za-z]+(?:['-][A-Za-z]+)*/g) || [];
  return matches.length === 1 ? matches[0].toLowerCase() : null;
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

const getLookupPopupPosition = (rect: DOMRect): LookupPosition => {
  const margin = 12;
  const gap = 10;
  const popupWidth = Math.min(420, window.innerWidth * 0.9);
  const estimatedPopupHeight = 260;
  const maxPopupHeight = Math.min(560, window.innerHeight * 0.7);

  const x = clamp(
    rect.left + rect.width / 2,
    popupWidth / 2 + margin,
    window.innerWidth - popupWidth / 2 - margin,
  );
  const spaceAbove = rect.top - gap - margin;
  const spaceBelow = window.innerHeight - rect.bottom - gap - margin;
  const placement =
    spaceAbove >= estimatedPopupHeight || spaceAbove >= spaceBelow
      ? "above"
      : "below";
  const availableHeight = placement === "above" ? spaceAbove : spaceBelow;
  const maxHeight = Math.max(80, Math.min(availableHeight, maxPopupHeight));

  return {
    x,
    y:
      placement === "above"
        ? clamp(rect.top - gap, margin + maxHeight, window.innerHeight - margin)
        : clamp(
            rect.bottom + gap,
            margin,
            window.innerHeight - margin - maxHeight,
          ),
    placement,
    maxHeight,
  };
};

const normalizeInjectedVocab = (values: unknown[]): string[] =>
  values
    .map((entry) => (typeof entry === "string" ? entry.trim() : ""))
    .filter(Boolean);

const getDefinitionText = (wordData: DictionaryLookupData): string => {
  const definition = wordData.meanings?.[0]?.definitions?.[0]?.definition;
  return definition || wordData.message || "No definition available.";
};

const getFunctionErrorMessage = async (error: any): Promise<string> => {
  const fallback = error?.message || "Error fetching definition.";

  try {
    if (error?.context?.json) {
      const body = await error.context.json();
      return body?.message || body?.error || fallback;
    }
  } catch {
    return fallback;
  }

  return fallback;
};

const getDefinitionSourceLabel = (source: string | undefined): string => {
  if (source === "merriam_webster") return "Merriam-Webster";
  if (source === "free_dictionary") return "Free Dictionary";
  return "Dictionary";
};

const getUniqueTerms = (values: Array<string | undefined> = []): string[] =>
  Array.from(new Set(values.filter(Boolean) as string[])).slice(0, 6);

const LookupResult: React.FC<{
  data: DictionaryLookupData | null;
  fallbackDefinition: string;
  lookupWord: string;
  saveControl: React.ReactNode;
}> = ({ data, fallbackDefinition, lookupWord, saveControl }) => {
  if (!data) {
    return (
      <div className="flex items-start justify-between gap-3">
        <p className="flex-1">{fallbackDefinition}</p>
        {saveControl}
      </div>
    );
  }

  const pronunciations = data.pronunciations || [];
  const audioUrl = pronunciations.find((item) => item.audio_url)?.audio_url;

  return (
    <div className="space-y-3">
      <div>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <strong className="text-lg text-indigo-800">
                {data.word || data.matched_word || lookupWord}
              </strong>
              {data.matched_word && data.matched_word !== data.word && (
                <span className="text-xs text-gray-500">
                  matched {data.matched_word}
                </span>
              )}
              <span className="text-xs font-medium text-gray-500">
                {getDefinitionSourceLabel(data.definition_source)}
              </span>
            </div>
          </div>
          <div className="flex shrink-0 items-center justify-end gap-2">
            {(data.phonetic || pronunciations.length > 0) && (
              <>
                {audioUrl && (
                  <button
                    type="button"
                    onClick={() => new Audio(audioUrl).play()}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-indigo-600 text-xs text-white hover:bg-indigo-700"
                    title="Play pronunciation"
                    aria-label="Play pronunciation"
                  >
                    ▶
                  </button>
                )}
                {getUniqueTerms([
                  data.phonetic,
                  ...pronunciations.map((item) => item.text),
                ])
                  .slice(0, 2)
                  .map((text) => (
                    <span
                      key={text}
                      className="rounded bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700"
                    >
                      {text}
                    </span>
                  ))}
              </>
            )}
            {saveControl}
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {(data.meanings || []).slice(0, 2).map((meaning, meaningIndex) => {
          const synonyms = getUniqueTerms([
            ...(meaning.synonyms || []),
            ...(meaning.definitions || []).flatMap(
              (definition) => definition.synonyms || [],
            ),
          ]);
          const antonyms = getUniqueTerms([
            ...(meaning.antonyms || []),
            ...(meaning.definitions || []).flatMap(
              (definition) => definition.antonyms || [],
            ),
          ]);

          return (
            <div
              key={`${meaning.partOfSpeech || "meaning"}-${meaningIndex}`}
              className="border-t border-gray-100 pt-3 first:border-t-0 first:pt-0"
            >
              {meaning.partOfSpeech && (
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {meaning.partOfSpeech}
                </div>
              )}
              <ol className="list-decimal space-y-2 pl-5">
                {(meaning.definitions || [])
                  .slice(0, 3)
                  .map((definition, definitionIndex) => (
                    <li key={definitionIndex}>
                      <span>{definition.definition}</span>
                      {definition.example && (
                        <div className="mt-1 text-xs italic text-gray-500">
                          {definition.example}
                        </div>
                      )}
                    </li>
                  ))}
              </ol>
              {(synonyms.length > 0 || antonyms.length > 0) && (
                <div className="mt-3 space-y-2 text-xs">
                  {synonyms.length > 0 && (
                    <div>
                      <span className="font-semibold text-gray-600">
                        Similar:
                      </span>{" "}
                      <span className="text-gray-600">
                        {synonyms.join(", ")}
                      </span>
                    </div>
                  )}
                  {antonyms.length > 0 && (
                    <div>
                      <span className="font-semibold text-gray-600">
                        Opposite:
                      </span>{" "}
                      <span className="text-gray-600">
                        {antonyms.join(", ")}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Reader: React.FC<ReaderProps> = ({
  heading,
  simplifiedContent,
  injectedVocab,
  userId,
  onFinishReading,
  originalContentOverride,
  readingSectionId,
  adjustedReadingSectionId,
  adaptationLevelNotes,
  sourceUrl,
  sourceDomain,
  debugModeEnabled = false,
  targetLevel,
  backLabel = "Back to Library",
  hasPreviousSection = false,
  hasNextSection = false,
  onPreviousSection,
  onNextSection,
}) => {
  const normalizedInjectedVocab = useMemo(
    () => normalizeInjectedVocab(injectedVocab),
    [injectedVocab],
  );

  // State for selected word lookup
  const [lookupWord, setLookupWord] = useState<string | null>(null);
  const [lookupDefinition, setLookupDefinition] = useState<string>("");
  const [lookupData, setLookupData] = useState<DictionaryLookupData | null>(
    null,
  );
  const [lookupStatus, setLookupStatus] = useState<LookupStatus>("idle");
  const [lookupCache, setLookupCache] = useState<Record<string, CachedLookup>>(
    {},
  );
  const [lookupPosition, setLookupPosition] = useState<LookupPosition | null>(
    null,
  );
  const [lookupSaving, setLookupSaving] = useState<boolean>(false);

  // State for reading completion
  const [readingCompleted, setReadingCompleted] = useState<boolean>(false);
  const [articleReadLogged, setArticleReadLogged] = useState<boolean>(false);

  // State for highlighting toggle
  const [highlightingEnabled, setHighlightingEnabled] = useState<boolean>(true);

  // State for original text toggle
  const [showOriginalText, setShowOriginalText] = useState<boolean>(false);
  const originalContent = originalContentOverride;
  const [readingPreferences, setReadingPreferences] = useState(
    getReadingPreferences,
  );
  const [readerSettingsOpen, setReaderSettingsOpen] = useState<boolean>(false);
  const {
    adjustedScrollRef,
    handleAdjustedScroll,
    adjustedScrollKey,
    clearAdjustedReadingPosition,
  } = useReadingPosition({
    userId,
    sectionId: readingSectionId,
    adjustedReadingSectionId,
    adjustedText: simplifiedContent,
    layoutKey: `${showOriginalText ? "side-by-side" : "adjusted-only"}:${readingPreferences.fontFamily}:${readingPreferences.fontSize}`,
  });

  // State for vocabulary saving
  const [savedWords, setSavedWords] = useState<Set<string>>(new Set());
  const [showSaveNotification, setShowSaveNotification] =
    useState<boolean>(false);
  const [lastSavedWord, setLastSavedWord] = useState<string>("");

  const readerTextClassName = `text-gray-800 leading-relaxed select-text relative ${getReadingFontSizeClass(
    readingPreferences.fontSize,
  )}`;
  const readerDiffClassName = debugModeEnabled
    ? "[&_del]:rounded-sm [&_del]:bg-rose-200 [&_del]:px-0.5 [&_del]:text-rose-950 [&_ins]:rounded-sm [&_ins]:bg-emerald-200 [&_ins]:px-0.5 [&_ins]:text-emerald-950 [&_ins]:no-underline"
    : "";
  const readerTextStyle: React.CSSProperties = {
    fontFamily: getReadingFontFamilyStyle(readingPreferences.fontFamily),
  };

  const updateReaderPreference = (
    updates: Partial<ReadingPreferences>,
  ): void => {
    const nextPreferences = {
      ...readingPreferences,
      ...updates,
    };

    setReadingPreferences(nextPreferences);
    saveReadingPreferences(nextPreferences);
  };

  const readerTheme = readingPreferences.theme;
  const readerPanelClassName = `rounded-lg shadow-lg p-4 transition-colors sm:p-8 ${
    readerTheme === "dark"
      ? "bg-gray-900 text-gray-100"
      : readerTheme === "sepia"
        ? "bg-amber-50 text-stone-900"
        : "bg-white text-gray-900"
  }`;
  const readerSubtleTextClassName =
    readerTheme === "dark"
      ? "text-gray-300"
      : readerTheme === "sepia"
        ? "text-stone-600"
        : "text-gray-600";
  const readerHeadingClassName = `text-sm font-semibold uppercase tracking-wide ${
    readerTheme === "dark" ? "text-gray-300" : "text-gray-500"
  }`;
  const readerBorderClassName =
    readerTheme === "dark"
      ? "border-gray-700"
      : readerTheme === "sepia"
        ? "border-amber-200"
        : "border-gray-200";
  const readerSelectClassName =
    "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500";

  useEffect(() => {
    const handleReadingPreferencesUpdated = () => {
      setReadingPreferences(getReadingPreferences());
    };

    window.addEventListener(
      READING_PREFERENCES_UPDATED_EVENT,
      handleReadingPreferencesUpdated,
    );
    window.addEventListener("storage", handleReadingPreferencesUpdated);

    return () => {
      window.removeEventListener(
        READING_PREFERENCES_UPDATED_EVENT,
        handleReadingPreferencesUpdated,
      );
      window.removeEventListener("storage", handleReadingPreferencesUpdated);
    };
  }, []);

  useEffect(() => {
    const fetchSavedWords = async () => {
      const { data, error } = await supabase
        .from("review_vocab")
        .select("word")
        .eq("user_id", userId);

      if (error) {
        console.error("Error fetching saved vocabulary:", error);
        return;
      }

      setSavedWords(
        new Set(
          (data || [])
            .map((item) =>
              typeof item.word === "string" ? item.word.toLowerCase() : "",
            )
            .filter(Boolean),
        ),
      );
    };

    fetchSavedWords();
  }, [userId]);

  // Function to highlight collocation phrases in the text
  const highlightPhrases = useCallback((text: string): string => {
    let highlightedText = text;

    collocations.forEach((phrase: string) => {
      const regex = new RegExp(`\\b${escapeRegExp(phrase)}\\b`, "gi");
      highlightedText = highlightedText.replace(regex, (match) => {
        return `<span class="phrase-highlight">${match}</span>`;
      });
    });

    return highlightedText;
  }, []);

  const highlightSavedVocab = useCallback(
    (text: string): string => {
      const savedVocabWords = Array.from(savedWords).sort(
        (a, b) => b.length - a.length,
      );

      if (savedVocabWords.length === 0) {
        return text;
      }

      const savedVocabPattern = new RegExp(
        `\\b(${savedVocabWords.map(escapeRegExp).join("|")})\\b`,
        "gi",
      );

      return text
        .split(/(<[^>]+>)/g)
        .map((part) => {
          if (part.startsWith("<") && part.endsWith(">")) {
            return part;
          }

          return part.replace(savedVocabPattern, (match) => {
            const normalizedWord = match.toLowerCase();
            return `<span class="vocab-word saved" data-word="${escapeHtmlAttribute(
              normalizedWord,
            )}" title="Saved vocabulary">${match}</span>`;
          });
        })
        .join("");
    },
    [savedWords],
  );

  // Function to convert markdown-style headers to HTML headers
  const convertHeaders = useCallback((text: string): string => {
    // Convert ## Section Title to <h2>Section Title</h2> with proper spacing
    let converted = text.replace(
      /^##\s+(.+)$/gm,
      '\n<h2 class="text-2xl font-bold text-gray-800 mt-8 mb-4 pb-2 border-b border-gray-200">$1</h2>\n',
    );

    // Convert ### Subsection Title to <h3>Subsection Title</h3> with proper spacing
    converted = converted.replace(
      /^###\s+(.+)$/gm,
      '\n<h3 class="text-xl font-semibold text-gray-700 mt-6 mb-3">$1</h3>\n',
    );

    // Convert #### Sub-subsection Title to <h4>Sub-subsection Title</h4> with proper spacing
    converted = converted.replace(
      /^####\s+(.+)$/gm,
      '\n<h4 class="text-lg font-medium text-gray-600 mt-4 mb-2">$1</h4>\n',
    );

    // Also handle any remaining MediaWiki-style headers that might not have been cleaned
    converted = converted.replace(
      /^={2,6}\s*([^=]+)\s*={2,6}$/gm,
      '\n<h2 class="text-2xl font-bold text-gray-800 mt-8 mb-4 pb-2 border-b border-gray-200">$1</h2>\n',
    );

    // Ensure paragraphs are properly separated with double line breaks
    converted = converted.replace(/\n{3,}/g, "\n\n"); // More than 2 newlines becomes 2
    converted = converted.replace(/([^\n])\n([^\n])/g, "$1\n\n$2"); // Add spacing between paragraphs

    // Convert single line breaks to <br> tags for proper spacing
    converted = converted.replace(/\n/g, "<br>\n");

    return converted;
  }, []);

  const processReaderContent = useCallback(
    (content: string, enableHighlights: boolean): string => {
      let html = convertHeaders(content);

      if (enableHighlights) {
        html = highlightSavedVocab(highlightPhrases(html));
      }

      return sanitizeReaderHtml(html);
    },
    [convertHeaders, highlightPhrases, highlightSavedVocab],
  );

  const simplifiedReaderHtml = useMemo(
    () => processReaderContent(simplifiedContent, highlightingEnabled),
    [simplifiedContent, highlightingEnabled, processReaderContent],
  );

  const originalReaderHtml = useMemo(
    () =>
      processReaderContent(
        originalContent || "Original text is not available yet.",
        false,
      ),
    [originalContent, processReaderContent],
  );

  const debugDiff = useMemo(() => {
    if (!debugModeEnabled || !showOriginalText || !originalContent) return null;

    try {
      const result = buildReaderDiff(originalReaderHtml, simplifiedReaderHtml);
      if (!result) return null;
      return {
        originalHtml: sanitizeReaderDiffHtml(result.originalHtml),
        adjustedHtml: sanitizeReaderDiffHtml(result.adjustedHtml),
      };
    } catch (error) {
      console.error("Reader debug comparison failed:", error);
      return null;
    }
  }, [
    debugModeEnabled,
    originalContent,
    originalReaderHtml,
    showOriginalText,
    simplifiedReaderHtml,
  ]);

  const displayedOriginalHtml = debugDiff?.originalHtml ?? originalReaderHtml;
  const displayedAdjustedHtml = debugDiff?.adjustedHtml ?? simplifiedReaderHtml;

  const lookupDefinitionForWord = async (
    selectedWord: string,
    rect: DOMRect,
  ) => {
    setLookupPosition(getLookupPopupPosition(rect));
    setLookupWord(selectedWord);

    const cachedLookup = lookupCache[selectedWord];
    if (cachedLookup) {
      setLookupDefinition(cachedLookup.definition);
      setLookupData(cachedLookup.data || null);
      setLookupStatus(cachedLookup.status);
      return;
    }

    setLookupDefinition("");
    setLookupData(null);
    setLookupStatus("loading");

    try {
      const { data, error } = await supabase.functions.invoke(
        "getWordDefinition",
        {
          body: {
            word: selectedWord,
            user_id: userId,
          },
        },
      );
      if (error) {
        console.error("Definition lookup error:", error);
        const message = await getFunctionErrorMessage(error);
        setLookupDefinition(message);
        setLookupData(null);
        setLookupStatus("error");
        setLookupCache((prev) => ({
          ...prev,
          [selectedWord]: {
            definition: message,
            data: null,
            status: "error",
          },
        }));
      } else {
        const wordData = data as DictionaryLookupData;
        const definition = getDefinitionText(wordData);
        setLookupDefinition(definition);
        setLookupData(wordData);
        setLookupStatus("success");
        setLookupCache((prev) => ({
          ...prev,
          [selectedWord]: {
            definition,
            data: wordData,
            status: "success",
          },
        }));
      }
    } catch (err) {
      console.error("Definition lookup exception:", err);
      const message = "Error fetching definition.";
      setLookupDefinition(message);
      setLookupData(null);
      setLookupStatus("error");
      setLookupCache((prev) => ({
        ...prev,
        [selectedWord]: { definition: message, data: null, status: "error" },
      }));
    }
  };

  // Function to handle saved vocabulary highlight clicks
  const handleSavedVocabClick = async (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    const vocabTarget = target.closest(".vocab-word") as HTMLElement | null;
    if (!vocabTarget) return;

    event.preventDefault();
    event.stopPropagation();
    window.getSelection()?.removeAllRanges();

    const word = vocabTarget.getAttribute("data-word");
    if (word) {
      await lookupDefinitionForWord(word, vocabTarget.getBoundingClientRect());
    }
  };

  // Handle user text selection for dictionary lookup
  const handleTextSelect = async () => {
    const selectionObject = window.getSelection();
    const selection = selectionObject?.toString().trim() || "";
    const selectedWord = extractSingleLookupWord(selection);

    // Only lookup a single English word.
    if (selectedWord && selectionObject?.rangeCount) {
      const rect = selectionObject.getRangeAt(0).getBoundingClientRect();
      await lookupDefinitionForWord(selectedWord, rect);
    }
  };

  // Handle saving looked-up word to vocabulary
  const handleSaveLookupWord = async () => {
    if (!lookupWord) return;

    setLookupSaving(true);
    try {
      // Check if word already exists to increment search count
      const { data: existingWord } = await supabase
        .from("review_vocab")
        .select("search_count")
        .eq("user_id", userId)
        .eq("word", lookupWord.toLowerCase())
        .single();

      const newSearchCount = (existingWord?.search_count || 0) + 1;

      const lookupContext = `User reading: ${
        sourceDomain || sourceUrl || readingSectionId
      }`;

      const { error } = await supabase.from("review_vocab").upsert(
        {
          user_id: userId,
          word: lookupWord.toLowerCase(),
          context: lookupContext,
          search_count: newSearchCount,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id,word" },
      );

      if (error) {
        console.error("Error saving word:", error);
      } else {
        setSavedWords((prev) => new Set(prev).add(lookupWord.toLowerCase()));
        setLastSavedWord(lookupWord);
        setShowSaveNotification(true);
        setTimeout(() => setShowSaveNotification(false), 3000);
      }
    } catch (err) {
      console.error("Error saving word:", err);
    } finally {
      setLookupSaving(false);
    }
  };

  // Effect to handle click outside popup
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Don't close if clicking on the popup itself
      if (target.closest(".lookup-popup")) {
        return;
      }
      // Don't close if clicking on saved vocabulary highlights
      if (target.closest(".vocab-word")) {
        return;
      }
      // Close popup if clicking elsewhere
      if (lookupWord) {
        setLookupWord(null);
        setLookupDefinition("");
        setLookupData(null);
        setLookupStatus("idle");
        setLookupPosition(null);
      }
    };

    if (lookupWord) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [lookupWord]);

  const logUserReadingRead = async (): Promise<boolean> => {
    if (articleReadLogged) return true;

    try {
      const progressPayload = {
        reading_section_id: readingSectionId,
        adjusted_reading_section_id: adjustedReadingSectionId,
        event_type: "article_read",
        event_data: {
          content_length: simplifiedContent.length,
          vocab_count: normalizedInjectedVocab.length,
          source_type: "user_reading",
        },
      };

      const { data, error } = await supabase.functions.invoke("logProgress", {
        body: progressPayload,
      });

      if (error) {
        console.error("Error logging user reading completion:", error);
        return false;
      }

      const completion = data as ReadingCompletionResponse | null;
      if (completion?.completed !== true) {
        console.error("Reading completion was not confirmed by the server.");
        return false;
      }

      setArticleReadLogged(true);
      setReadingCompleted(true);
      return true;
    } catch (error) {
      console.error("Error logging user reading completion:", error);
      return false;
    }
  };

  const handleCompleteReading = async () => {
    if (readingCompleted) return;

    const completed = await logUserReadingRead();
    if (!completed) return;

    clearAdjustedReadingPosition();
    onFinishReading();
  };

  const handleBackToLibrary = () => {
    onFinishReading();
  };

  const renderLookupSaveButton = () => {
    if (!lookupWord) return null;

    const wordIsSaved = savedWords.has(lookupWord.toLowerCase());

    return (
      <button
        onClick={handleSaveLookupWord}
        disabled={lookupSaving || wordIsSaved}
        className={`p-1 rounded transition-colors ${
          wordIsSaved
            ? "text-green-600 cursor-default"
            : lookupSaving
              ? "text-yellow-600 cursor-default"
              : "text-gray-400 hover:text-indigo-600"
        }`}
        title={wordIsSaved ? "Already saved" : "Save to vocabulary"}
      >
        {wordIsSaved ? (
          "★"
        ) : lookupSaving ? (
          <span className="animate-spin">⟳</span>
        ) : (
          "☆"
        )}
      </button>
    );
  };

  // Render the imported reading with vocabulary and comparison controls.
  return (
    <div
      className={`reader reader-theme-${readerTheme} mx-auto p-4 sm:p-6 ${
        showOriginalText ? "max-w-7xl" : "max-w-4xl"
      }`}
    >
      <h1 className="sr-only" data-route-focus tabIndex={-1}>
        {heading}
      </h1>
      {/* Save notification */}
      {showSaveNotification && (
        <div className="fixed left-4 right-4 top-4 z-50 animate-bounce rounded-lg bg-green-500 px-4 py-2 text-white shadow-lg sm:left-auto">
          <div className="flex items-center gap-2">
            <span>✓</span>
            <span>"{lastSavedWord}" saved to vocabulary!</span>
          </div>
        </div>
      )}

      <div className={readerPanelClassName}>
        {/* Reading progress indicator and controls */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-green-500"></div>
              <span className={`text-sm ${readerSubtleTextClassName}`}>
                {readingCompleted ? "Reading completed" : "Reading in progress"}
              </span>
            </div>

            {debugModeEnabled && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-violet-100 px-2.5 py-1 text-xs font-semibold text-violet-800">
                  DEBUG
                </span>
                {targetLevel && (
                  <span
                    className={`text-xs font-medium ${readerSubtleTextClassName}`}
                  >
                    Target level: {targetLevel}
                  </span>
                )}
              </div>
            )}

            {/* Highlighting toggle */}
            <div className="flex items-center gap-2">
              <label className={`text-sm ${readerSubtleTextClassName}`}>
                Highlights:
              </label>
              <button
                onClick={() => setHighlightingEnabled(!highlightingEnabled)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  highlightingEnabled
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {highlightingEnabled ? "ON" : "OFF"}
              </button>
            </div>

            {/* Original text toggle */}
            <div className="flex items-center gap-2">
              <label className={`text-sm ${readerSubtleTextClassName}`}>
                Text:
              </label>
              <button
                onClick={() => setShowOriginalText(!showOriginalText)}
                aria-pressed={showOriginalText}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  showOriginalText
                    ? "bg-blue-100 text-blue-800"
                    : "bg-purple-100 text-purple-800"
                }`}
              >
                {showOriginalText ? "Hide original" : "Show original"}
              </button>
            </div>
          </div>

          <div className="relative flex items-center justify-end gap-3">
            {readingCompleted && (
              <span className="text-sm font-medium text-green-600">
                ✓ Completed
              </span>
            )}
            <button
              type="button"
              onClick={() => setReaderSettingsOpen((isOpen) => !isOpen)}
              className={`inline-flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${
                readerTheme === "dark"
                  ? "border-gray-700 text-gray-200 hover:bg-gray-800"
                  : "border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
              aria-label="Reader settings"
              aria-expanded={readerSettingsOpen}
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.591 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.592c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.591c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.591 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.592-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.591c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.592c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.592-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </button>

            {readerSettingsOpen && (
              <div className="absolute right-0 top-11 z-40 w-72 rounded-lg border border-gray-200 bg-white p-4 text-left shadow-xl">
                <div className="mb-3 text-sm font-semibold text-gray-800">
                  Reader Settings
                </div>
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="reader-font-family"
                      className="mb-1 block text-xs font-medium text-gray-600"
                    >
                      Font
                    </label>
                    <select
                      id="reader-font-family"
                      value={readingPreferences.fontFamily}
                      onChange={(event) =>
                        updateReaderPreference({
                          fontFamily: event.target.value as ReadingFontFamily,
                        })
                      }
                      className={readerSelectClassName}
                    >
                      <option value="system">System</option>
                      <option value="serif">Serif</option>
                      <option value="sans">Sans-serif</option>
                      <option value="mono">Monospace</option>
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="reader-font-size"
                      className="mb-1 block text-xs font-medium text-gray-600"
                    >
                      Size
                    </label>
                    <select
                      id="reader-font-size"
                      value={readingPreferences.fontSize}
                      onChange={(event) =>
                        updateReaderPreference({
                          fontSize: event.target.value,
                        })
                      }
                      className={readerSelectClassName}
                    >
                      <option value="small">Small</option>
                      <option value="medium">Medium</option>
                      <option value="large">Large</option>
                      <option value="extra-large">Extra Large</option>
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="reader-theme"
                      className="mb-1 block text-xs font-medium text-gray-600"
                    >
                      Theme
                    </label>
                    <select
                      id="reader-theme"
                      value={readingPreferences.theme}
                      onChange={(event) =>
                        updateReaderPreference({
                          theme: event.target.value as ReadingTheme,
                        })
                      }
                      className={readerSelectClassName}
                    >
                      <option value="light">Light</option>
                      <option value="dark">Dark</option>
                      <option value="sepia">Sepia</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {debugModeEnabled && adaptationLevelNotes && (
          <section
            className={`mb-4 rounded-md border px-4 py-3 text-sm leading-relaxed ${readerBorderClassName} ${readerSubtleTextClassName}`}
            aria-labelledby="reader-gpt-notes"
          >
            <h2 id="reader-gpt-notes" className="mb-1 font-semibold">
              GPT notes
            </h2>
            <p>{adaptationLevelNotes}</p>
          </section>
        )}

        {/* Article content */}
        {showOriginalText ? (
          <>
            {debugModeEnabled && (
              <div className="mb-3 flex flex-wrap gap-4 text-xs">
                <span className="rounded bg-rose-100 px-2 py-1 text-rose-800">
                  Removed from Original
                </span>
                <span className="rounded bg-emerald-100 px-2 py-1 text-emerald-800">
                  Added to Adjusted
                </span>
              </div>
            )}
            <div className="mb-8 grid gap-6 lg:grid-cols-2">
              <section className="min-w-0">
                <div
                  className={`mb-3 flex items-center justify-between border-b pb-2 ${readerBorderClassName}`}
                >
                  <h2 className={readerHeadingClassName}>Adjusted</h2>
                </div>
                <div
                  key={adjustedScrollKey}
                  ref={adjustedScrollRef}
                  onScroll={handleAdjustedScroll}
                  data-testid="adjusted-reading-scroll"
                  className="prose prose-lg max-h-[calc(100vh-18rem)] max-w-none overflow-y-auto pr-3"
                >
                  <div
                    className={`${readerTextClassName} ${readerDiffClassName}`}
                    style={readerTextStyle}
                    dangerouslySetInnerHTML={{ __html: displayedAdjustedHtml }}
                    onClick={handleSavedVocabClick}
                    onMouseUp={handleTextSelect}
                  />
                </div>
              </section>

              <section
                className={`min-w-0 border-t pt-6 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0 ${readerBorderClassName}`}
              >
                <div
                  className={`mb-3 flex items-center justify-between border-b pb-2 ${readerBorderClassName}`}
                >
                  <h2 className={readerHeadingClassName}>Original</h2>
                </div>
                <div
                  data-testid="original-reading-scroll"
                  className="prose prose-lg max-h-[calc(100vh-18rem)] max-w-none overflow-y-auto pr-3"
                >
                  <div
                    className={`${readerTextClassName} ${readerDiffClassName}`}
                    style={readerTextStyle}
                    dangerouslySetInnerHTML={{ __html: displayedOriginalHtml }}
                    onMouseUp={handleTextSelect}
                  />
                </div>
              </section>
            </div>
          </>
        ) : (
          <div
            key={adjustedScrollKey}
            ref={adjustedScrollRef}
            onScroll={handleAdjustedScroll}
            data-testid="adjusted-reading-scroll"
            className="prose prose-lg mb-8 max-h-[calc(100vh-18rem)] max-w-none overflow-y-auto pr-3"
          >
            <div
              className={readerTextClassName}
              style={readerTextStyle}
              dangerouslySetInnerHTML={{ __html: simplifiedReaderHtml }}
              onClick={handleSavedVocabClick}
              onMouseUp={handleTextSelect}
            />
          </div>
        )}

        {/* Word Lookup Popup */}
        {lookupWord && lookupPosition && (
          <div
            className="fixed lookup-popup z-50"
            style={{
              left: lookupPosition.x,
              top: lookupPosition.y,
              maxHeight: lookupPosition.maxHeight,
              transform:
                lookupPosition.placement === "above"
                  ? "translate(-50%, -100%)"
                  : "translate(-50%, 0)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`text-sm mb-2 ${
                lookupStatus === "error" ? "text-red-700" : "text-gray-700"
              }`}
            >
              {lookupStatus === "loading" ? (
                <span className="inline-flex items-center gap-2">
                  <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
                  Loading definition...
                </span>
              ) : lookupStatus === "success" ? (
                <LookupResult
                  data={lookupData}
                  fallbackDefinition={lookupDefinition}
                  lookupWord={lookupWord}
                  saveControl={renderLookupSaveButton()}
                />
              ) : (
                <div className="flex items-start justify-between gap-3">
                  <span className="flex-1">{lookupDefinition}</span>
                  {renderLookupSaveButton()}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between gap-3">
              {lookupStatus === "success" && lookupData?.sourceUrls?.[0] ? (
                <a
                  href={lookupData.sourceUrls[0]}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
                >
                  View source
                </a>
              ) : (
                <span />
              )}
              <button
                onClick={() => {
                  setLookupWord(null);
                  setLookupDefinition("");
                  setLookupData(null);
                  setLookupStatus("idle");
                  setLookupPosition(null);
                }}
                className="text-xs text-gray-500 hover:text-gray-800 px-2 py-1 rounded hover:bg-gray-100"
              >
                Close
              </button>
            </div>
          </div>
        )}

        <ReadingFeedback
          key={adjustedReadingSectionId}
          userId={userId}
          adjustedReadingSectionId={adjustedReadingSectionId}
          theme={readerTheme}
        />

        {/* Action buttons */}
        <div className="flex flex-col gap-4 pt-6 border-t border-gray-200 sm:flex-row sm:items-center sm:justify-between">
          <button
            onClick={handleBackToLibrary}
            className="px-4 py-2 text-gray-600 transition-colors hover:text-gray-800"
          >
            ← {backLabel}
          </button>

          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <button
              onClick={onPreviousSection}
              disabled={!hasPreviousSection}
              className="rounded-lg border border-gray-200 px-4 py-2 text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={onNextSection}
              disabled={!hasNextSection}
              className="rounded-lg border border-gray-200 px-4 py-2 text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>

          <button
            type="button"
            onClick={handleCompleteReading}
            disabled={readingCompleted}
            className="rounded-lg bg-indigo-600 px-6 py-3 font-medium text-white transition-colors hover:bg-indigo-700 disabled:cursor-default disabled:bg-green-600"
          >
            {readingCompleted ? "Completed" : "Complete Reading"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Reader;

/*
Suggested CSS for highlighting:

.phrase-chunk {
  background-color: #fff3cd;
  padding: 2px 4px;
  border-radius: 3px;
  font-weight: 500;
}

.vocab-highlight {
  background-color: #d1ecf1;
  padding: 2px 4px;
  border-radius: 3px;
  font-weight: 600;
  color: #0c5460;
}

.reader {
  font-family: 'Georgia', serif;
  line-height: 1.8;
}

.reader .prose {
  font-size: 1.1rem;
}


.lookup-popup {
  max-width: 80%;
  word-break: break-word;
}

*/
